import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Annotation } from '@shared/models/annotation.model';
import { QuizType } from '@shared/models';
import { AnalyticsService, chartColorPalette } from '@shared/services/analytics.service';
import { GoogleChartInterface } from 'ng2-google-charts';
import { LoadState } from '@app/main/analytics/loader-directive/loader.module';
import { QuizInputProperties, QuizChoicesProperties } from '@shared/models';
import { formatPercent } from '@angular/common';

interface QuizResultData {
  _id: string,
  count: number
}
@Component({
  selector: 'quiz-chart',
  template: `<mat-card class="quiz-chart" *loading="loadStatus">
                <div class="quiz-data">
                  <div class="top">
                      <div class="label">{{ quiz.label}}</div>
                      <div class="tag">{{ quiz.properties.quiz.quiz_type === 'input' ? 'Free Input Quiz' : 'Single Choice Question'}}</div>
                    </div>
            
                    <div class="question">
                      {{ quiz.properties.quiz.question }}
                    </div>
                </div>
                <div class="chart">
                  <google-chart *ngIf="chart; else noResult;" [data]="chart"></google-chart>
                  <ng-template #noResult>
                    <div>
                      No Data Yet!
                    </div>
                  </ng-template>
                </div>
            </mat-card>
  `,
  styles: [
    `
    .quiz-chart{
      display: flex;
      height: 200px;
      margin-top: 16px;
    }
    .quiz-data{
      width: 35%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-family: 'Raleway', sans-serif;
      font-size: 16px;
    }
    .quiz-data .label{
      font-weight: bold;
    }
    
    .quiz-data .tag {
      margin-top: 8px;
      padding: 5px;
      font-size: 10px;
      background-color: #42424291;
      border-radius: 3px;
      width: fit-content;
      color: white;
    }
    
    .quiz-data .question{
      margin-top: 16px;
      font-size: 14px;
    }
    
    .chart{
      width: 65%;
      display: flex;
      align-items: center;
    }
    .chart google-chart{
      width: 80%;
    }
    `
  ]
})
export class QuizChartComponent implements OnInit, OnChanges {
  @Input() quiz: Annotation;
  @Input() date_from: Date;
  @Input() date_to: Date;
  @Input() search: string;
  public chart: GoogleChartInterface | null;
  public loadStatus: string;
  public defaultColor: string = "#247e8d";

  constructor(
    private _analyticsService: AnalyticsService
  ) {

  }
  ngOnInit(): void {
    this.updateStats();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.date_from && !changes.date_from.isFirstChange() && changes.date_from.currentValue !== changes.date_from.previousValue)
      || (changes.date_to && !changes.date_to.isFirstChange() && changes.date_to.currentValue !== changes.date_to.previousValue)
      || (changes.search && changes.search.currentValue !== changes.search.previousValue)) {
      this.updateStats();
    }
  }
  updateStats() {
    this.loadStatus = LoadState.loading;
    this._analyticsService.getQuizResults(this.quiz.id, { dateFrom: this.date_from, dateTo: this.date_to, userName: this.search }).subscribe((results: QuizResultData[]) => {
      this.loadStatus = LoadState.completed;
      if (results.length > 0) {
        this.chart = this.quiz.properties.quiz.quiz_type === QuizType.input ? this.getQuizInputChart(this.quiz.properties.quiz, results) : this.getQuizChoicesChart(this.quiz.properties.quiz, results);
      }
      else {
        this.chart = null;
      }


    }, () => {
      this.loadStatus = LoadState.error;
    });
  }
  getQuizChoicesChart(quizProperties: QuizChoicesProperties, quizResults: QuizResultData[]): GoogleChartInterface {
    let colorPaletteIndex = 0;
    const totalAnswers = quizResults.reduce((totalCount, currentRes) => totalCount + currentRes.count, 0);
    const dataArray = [];
    for (let ans of quizProperties.answers) {
      const result = quizResults.find((r) => r._id === ans.label);
      if (colorPaletteIndex === chartColorPalette.length) colorPaletteIndex = 0;
      const color = chartColorPalette[colorPaletteIndex++];
      dataArray.push([`${ans.label} (${result?.count || 0})`, result?.count || 0, formatPercent(result?.count / totalAnswers || 0, 'en-US'), color])
    }
    dataArray.unshift(['Response', 'Count', { role: 'annotation' }, { role: 'style' }]);

    return {
      chartType: 'BarChart',
      dataTable: dataArray,
      options: {
        animation: {
          duration: 200,
          easing: "inAndOut",
          startup: true,
        },
        chartArea: { left: "40%", width: "60%", height: "90%" },
        fontName: "Raleway",
        hAxis: {
          gridlines: {
            count: 0,
          },
          textStyle: {
            color: 'transparent'
          }
        },
        enableInteractivity: false,
        tooltip: {
          trigger: 'none'
        },
        legend: {
          position: 'none'
        }
      }
    };
  }
  getQuizInputChart(quizProperties: QuizInputProperties, quizResults: QuizResultData[]): GoogleChartInterface {
    const rightColor = this.defaultColor;
    const wrongColor = "#e2503e";
    const rightAnwsers = [];
    const wrongAnswers = [];
    const totalAnswers = quizResults.reduce((totalCount, currentRes) => totalCount + currentRes.count, 0);
    const quizAcceptedAnswers = quizProperties.accepted_answers ? quizProperties.accepted_answers.map((ans: any) => ans.value.toLowerCase()) : [];
    for (let result of quizResults) {
      if (quizAcceptedAnswers.indexOf(result._id) > -1) {
        rightAnwsers.push(result);
      }
      else {
        wrongAnswers.push(result);
      }
    }
    const rightAnwsersLabel = this.groupCountResults(rightAnwsers);
    const rightAnswersCount = this.groupSumResults(rightAnwsers);
    const rightAnswerAnnotation = formatPercent(rightAnswersCount / totalAnswers || 0, 'en-US')
    const wrongAnswersLabel = this.groupCountResults(wrongAnswers);
    const wrongAnswerCount = this.groupSumResults(wrongAnswers);
    const wrongAnswerAnnotation = formatPercent(wrongAnswerCount / totalAnswers || 0, 'en-US')
    const dataArray: any[] = [[rightAnwsersLabel, rightAnswersCount, rightColor, rightAnswerAnnotation], [wrongAnswersLabel, wrongAnswerCount, wrongColor, wrongAnswerAnnotation]]
    dataArray.unshift([
      "Response",
      "Count",
      { role: "style" },
      { role: "annotation" },
    ]);
    return {
      chartType: 'BarChart',
      dataTable: dataArray,
      options: {
        animation: {
          duration: 200,
          easing: "inAndOut",
          startup: true,
        },
        chartArea: { left: "40%", width: "60%", height: "90%" },
        fontName: "Raleway",
        hAxis: {
          gridlines: {
            count: 0,
          },
          textStyle: {
            color: "transparent",
          },
        },
        enableInteractivity: false,
        tooltip: {
          trigger: "none",
        },
        legend: {
          position: "none",
        },
      }
    };
  }
  groupCountResults(results: QuizResultData[]): string {
    return results.sort((a, b) => (a.count > b.count) ? -1 : 1).map((res) => `${res._id} (${res.count})`).join(', ');
  }
  groupSumResults(results: QuizResultData[]): number {
    return results.reduce((totalCount, currentRes) => totalCount + currentRes.count, 0);
  }
}
